import { useEffect, useState } from 'react';

interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
}

interface WakeLockNavigator {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
}

export type WakeLockState = 'unsupported' | 'active' | 'released';

/**
 * Keeps the screen on while `active` (Screen Wake Lock API) — a guided test
 * session must not go dark while the user waits with wet hands. The lock is
 * dropped by the browser when the tab is hidden and requested again when it is
 * visible. Without the API (or when refused) the state says so and the session
 * goes on without it.
 */
export function useWakeLock(active: boolean): WakeLockState {
  const supported = typeof navigator !== 'undefined' && Boolean((navigator as WakeLockNavigator).wakeLock);
  const [state, setState] = useState<WakeLockState>(supported ? 'released' : 'unsupported');

  useEffect(() => {
    if (!active || !supported) return undefined;
    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const request = async (): Promise<void> => {
      try {
        const lock = await (navigator as WakeLockNavigator).wakeLock!.request('screen');
        if (cancelled) {
          void lock.release();
          return;
        }
        sentinel = lock;
        setState('active');
        lock.addEventListener?.('release', () => {
          if (!cancelled) setState('released');
        });
      } catch {
        // Refused (battery saver, not visible): the session continues without it.
        if (!cancelled) setState('unsupported');
      }
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void request();
    };

    void request();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active, supported]);

  return state;
}
